
import { Link } from "react-router-dom";
import { ArrowUpAZ, ArrowDownAZ, ArrowUp01, ArrowDown01 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useIsMobile } from "@/hooks/use-mobile";

type Course = {
  id: string;
  course_name: string;
  course_type: string;
  description: string;
  duration_hours: number;
  price: number;
  language: string;
};

type SortConfig = {
  key: keyof Course;
  direction: 'asc' | 'desc';
} | null;

interface CoursesTableProps {
  courses: Course[];
  sortConfig: SortConfig;
  onSort: (key: keyof Course) => void;
}

export function CoursesTable({ courses, sortConfig, onSort }: CoursesTableProps) {
  const isMobile = useIsMobile();
  
  const getSortIcon = (key: keyof Course) => {
    if (sortConfig?.key !== key) {
      if (key === 'duration_hours' || key === 'price') {
        return <ArrowDown01 className="h-4 w-4 inline ml-1 text-gray-400" />;
      }
      return <ArrowDownAZ className="h-4 w-4 inline ml-1 text-gray-400" />;
    }

    if (key === 'duration_hours' || key === 'price') {
      return sortConfig.direction === 'asc' 
        ? <ArrowUp01 className="h-4 w-4 inline ml-1 text-primary" />
        : <ArrowDown01 className="h-4 w-4 inline ml-1 text-primary" />;
    }

    return sortConfig.direction === 'asc'
      ? <ArrowUpAZ className="h-4 w-4 inline ml-1 text-primary" />
      : <ArrowDownAZ className="h-4 w-4 inline ml-1 text-primary" />;
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead 
            className="cursor-pointer p-2 md:p-4"
            onClick={() => onSort('course_name')}
          >
            Course {!isMobile && "Name"} {getSortIcon('course_name')}
          </TableHead>
          {!isMobile && (
            <TableHead 
              className="cursor-pointer"
              onClick={() => onSort('course_type')}
            >
              Type {getSortIcon('course_type')}
            </TableHead>
          )}
          {!isMobile && <TableHead>Language</TableHead>}
          <TableHead 
            className="cursor-pointer p-2 md:p-4"
            onClick={() => onSort('duration_hours')}
          >
            {isMobile ? "Hours" : "Duration (hours)"} {getSortIcon('duration_hours')}
          </TableHead>
          <TableHead 
            className="cursor-pointer p-2 md:p-4"
            onClick={() => onSort('price')}
          >
            Price {getSortIcon('price')}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {courses.map((course) => (
          <TableRow key={course.id}>
            <TableCell className="p-2 md:p-4">
              <Link 
                to={`/courses/${course.id}`}
                className="text-primary hover:underline font-medium"
              >
                <div className="flex flex-col">
                  <span>{course.course_name}</span>
                  {isMobile && (
                    <span className="text-xs text-muted-foreground">
                      {course.course_type}
                    </span>
                  )}
                </div>
              </Link>
            </TableCell>
            {!isMobile && <TableCell>{course.course_type}</TableCell>}
            {!isMobile && <TableCell>{course.language || 'English'}</TableCell>}
            <TableCell className="p-2 md:p-4">{course.duration_hours}</TableCell>
            <TableCell className="p-2 md:p-4">${course.price.toFixed(2)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
